import React from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Button,
    IconButton,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Link, LinkProps, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

const StyledLink = styled(Link)<LinkProps>({
    textDecoration: "none",
    color: "inherit",
});

const NavLinkText = styled(Link)<LinkProps>({
    position: "relative",
    textDecoration: "none",
    color: "black",
    fontWeight: 500,
    padding: "6px 2px",
    transition: "color 0.2s ease",

    "&::after": {
        content: '""',
        position: "absolute",
        left: 0,
        bottom: 0,
        width: "0%",
        height: "2px",
        backgroundColor: "#1f5185",
        transition: "width 0.25s ease",
    },

    "&:hover": {
        color: "#1f5185",
    },

    "&:hover::after": {
        width: "100%",
    },
});

const Header: React.FC = () => {
    // Single source of truth for horizontal alignment with the rest of the site
    const gutterX = { xs: 2, sm: 3, md: 6, lg: 16 } as const;

    const [open, setOpen] = React.useState(false);

    const close = () => setOpen(false);
    const toggle = () => setOpen((v) => !v);

    const navigate = useNavigate();
    const location = useLocation();

    // Scrolls if already on home; otherwise redirects to home with hash.
    const goToContact = React.useCallback(() => {
        const onHome = location.pathname === "/";

        // Always ensure home has the hash so HomeRoute can scroll after navigation
        if (!onHome) {
            close();
            navigate("/#contact");
            return;
        }

        // Already on home: set hash (optional, but helps refresh/share link)
        if (location.hash !== "#contact") {
            navigate("/#contact", { replace: false });
        }

        close();
        requestAnimationFrame(() => {
            document
                .getElementById("contact-section")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }, [location.pathname, location.hash, navigate]);

    const goHome = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();

            close();

            const onHome = location.pathname === "/";

            if (onHome) {
                requestAnimationFrame(() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                });
                return;
            }

            navigate("/", { replace: false });
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: "auto" });
            });
        },
        [location.pathname, navigate]
    );

    return (
        <AppBar
            position="fixed"
            sx={{
                backgroundColor: "white",
                color: "black",
                boxShadow: 1,
                zIndex: 1100,
            }}
        >
            <Toolbar
                sx={{
                    px: gutterX,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    minHeight: { xs: 56, sm: 60 },
                    gap: 2,
                }}
            >
                {/* Left: Branding */}
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    <StyledLink to="/" onClick={goHome}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: "'Roboto', sans-serif",
                                fontWeight: 600,
                                transition: "color 0.2s ease",
                                "&:hover": { color: "#1f5185" },
                                // responsive text so it doesn't crush on small screens
                                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            Aggrey Lelei
                        </Typography>
                    </StyledLink>
                </Box>

                {/* Right: Desktop Navigation */}
                <Box
                    sx={{
                        display: { xs: "none", md: "flex" },
                        alignItems: "center",
                        gap: { md: 3, lg: 4 },
                        pr: { md: 0, lg: 0.5 },
                        flexShrink: 0,
                    }}
                >
                    <NavLinkText to="/projects">Projects</NavLinkText>
                    <NavLinkText to="/blog">Tech Blog</NavLinkText>

                    <Button
                        variant="contained"
                        startIcon={<MailOutlineIcon />}
                        sx={{
                            backgroundColor: "white",
                            background: "rgba(31,81,133,0.24)",
                            transition: "all 0.18s ease",
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { background: "#fff" },
                        }}
                        onClick={goToContact}
                    >
                        Contact
                    </Button>
                </Box>

                {/* Mobile: Menu + compact CTA */}
                <Box
                    sx={{
                        display: { xs: "flex", md: "none" },
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                    }}
                >
                    <IconButton
                        aria-label="Open menu"
                        onClick={toggle}
                        edge="end"
                        sx={{ color: "black" }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>
            </Toolbar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={open}
                onClose={close}
                PaperProps={{
                    sx: { width: "min(82vw, 340px)" },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Menu</Typography>
                </Box>
                <Divider />

                <List sx={{ py: 0 }}>
                    <ListItemButton component={Link} to="/portfolio" onClick={close}>
                        <ListItemText primary="Portfolio" />
                    </ListItemButton>
                    <ListItemButton component={Link} to="/blog" onClick={close}>
                        <ListItemText primary="Tech BlogHomePage" />
                    </ListItemButton>
                </List>

                <Box sx={{ p: 2, pt: 1 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<MailOutlineIcon />}
                        sx={{

                            background: "rgba(31,81,133,0.24)",
                            transition: "all 0.18s ease",
                            textTransform: "none",
                            fontWeight: 700,
                            "&:hover": { background: "#fff" },
                        }}
                        onClick={goToContact}
                    >
                        Contact
                    </Button>
                </Box>
            </Drawer>
        </AppBar>
    );
};

export default Header;
